import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Warehouse, 
  MapPin, 
  Package,
  AlertTriangle,
  Minus,
  Plus,
  ArrowRightLeft,
  ClipboardCheck
} from 'lucide-react';
import { locationsApi } from '@/api/locationsApi';
import { inventoryApi } from '@/api/inventoryApi';
import { EntityLink } from '@/components/detail/EntityLink';
import { formatDate } from '@/utils/formatters';
import { StockAdjustmentModal } from '@/components/inventory/StockAdjustmentModal';
import type { ShelfInventory } from '@/types/api';

export function Inventory() {
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [adjustModal, setAdjustModal] = useState<{ isOpen: boolean; item: ShelfInventory | null; type: 'add' | 'remove' | 'count' }>({
    isOpen: false,
    item: null,
    type: 'count',
  });

  const { data: locations, isLoading: locationsLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.getAll,
  });

  const { data: inventory, isLoading: inventoryLoading } = useQuery({
    queryKey: ['inventory', selectedLocation],
    queryFn: () => selectedLocation 
      ? inventoryApi.getByLocation(selectedLocation)
      : inventoryApi.getAll(),
  });

  const { data: lowStockItems } = useQuery({
    queryKey: ['low-stock'],
    queryFn: inventoryApi.getLowStock,
  });

  const displayedInventory = selectedLocation
    ? inventory?.filter(item => item.locationId === selectedLocation)
    : inventory;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-sans text-3xl font-bold text-gray-900 dark:text-warm-cream">Inventory</h1>
          <p className="text-gray-600 dark:text-warm-sand mt-1">
            Track what's on the shelves at each location
          </p>
        </div>
        <button type="button" onClick={() => window.location.assign('/transfers')} className="btn-secondary">
          <ArrowRightLeft className="w-5 h-5 mr-2" />
          Record Transfer
        </button>
      </div>

      {/* Location Selector */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          onClick={() => setSelectedLocation(null)}
          className={`card p-4 text-left transition-all ${
            selectedLocation === null 
              ? 'ring-2 ring-amber-450 bg-amber-450/5' 
              : 'hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-450/10 flex items-center justify-center">
              <Warehouse className="w-5 h-5 text-amber-450" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-warm-cream">All Locations</p>
              <p className="text-xs text-gray-600 dark:text-warm-sand">View combined inventory</p>
            </div>
          </div>
        </button>

        {locationsLoading ? (
          <div className="card p-4">
            <p className="text-gray-600 dark:text-warm-sand">Loading locations...</p>
          </div>
        ) : (
          locations?.map((location) => (
            <button
              key={location.id}
              onClick={() => setSelectedLocation(location.id)}
              className={`card p-4 text-left transition-all ${
                selectedLocation === location.id 
                  ? 'ring-2 ring-amber-450 bg-amber-450/5' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-warm-cream">{location.name}</p>
                  {location.managerName && (
                    <p className="text-xs text-gray-600 dark:text-warm-sand">Mgr: {location.managerName}</p>
                  )}
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Inventory List */}
      <div className="card">
        <div className="p-4 border-b border-gray-200 dark:border-charcoal-700">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <h2 className="font-sans text-lg font-semibold text-gray-900 dark:text-warm-cream">
              {selectedLocation 
                ? `Inventory at ${locations?.find(l => l.id === selectedLocation)?.name || 'Location'}`
                : 'All Inventory'
              }
            </h2>
            <div className="flex gap-2">
              <span className="badge-warning">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {lowStockItems?.length || 0} Low Stock
              </span>
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-warm-sand">
            Use Take Inventory on a row to record actual count, remarks, bin location, and bin number for that item/location.
          </p>
        </div>

        <div className="table-container border-0 rounded-t-none">
          <table className="table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Variant</th>
                <th>Location</th>
                <th>On Shelf</th>
                <th>Bin</th>
                <th>Remarks</th>
                <th>Reserved</th>
                <th>Available</th>
                <th>Last Counted</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {inventoryLoading ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-gray-600 dark:text-warm-sand">
                    Loading inventory...
                  </td>
                </tr>
              ) : displayedInventory?.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-gray-600 dark:text-warm-sand">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-charcoal-700 flex items-center justify-center">
                        <Package className="w-8 h-8 text-gray-600 dark:text-warm-sand" />
                      </div>
                      <p>No inventory items found</p>
                      <p className="text-sm">Select a location or add products first</p>
                    </div>
                  </td>
                </tr>
              ) : (
                displayedInventory?.map((item) => (
                  <tr key={item.id} className={item.quantityOnShelf < 5 ? 'bg-red-500/5' : ''}>
                    <td>
                      <EntityLink
                        type="product"
                        id={item.productId}
                        label={item.productName || item.variantSku?.split('-')[0] || 'Unknown'}
                      />
                    </td>
                    <td>
                      <p className="mono text-sm text-gray-600 dark:text-warm-sand">{item.variantSku}</p>
                    </td>
                    <td>
                      <EntityLink type="location" id={item.locationId} label={item.locationName} />
                    </td>
                    <td className="font-medium text-gray-900 dark:text-warm-cream">{item.quantityOnShelf}</td>
                    <td className="text-gray-600 dark:text-warm-sand text-sm">
                      {[item.binLocation, item.binNumber].filter(Boolean).join(' / ') || '-'}
                    </td>
                    <td className="max-w-xs truncate text-gray-600 dark:text-warm-sand text-sm" title={item.notes || undefined}>
                      {item.notes || '-'}
                    </td>
                    <td className="text-gray-600 dark:text-warm-sand">{item.quantityReserved}</td>
                    <td>
                      <span className={item.availableQuantity < 5 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-green-600 dark:text-green-400'}>
                        {item.availableQuantity}
                      </span>
                    </td>
                    <td className="text-gray-600 dark:text-warm-sand text-sm">
                      {item.lastCountedAt ? formatDate(item.lastCountedAt) : 'Never'}
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setAdjustModal({ isOpen: true, item, type: 'count' })}
                          className="p-2 hover:bg-blue-500/10 hover:text-blue-600 dark:text-blue-400 rounded-lg transition-colors"
                          title="Take Inventory"
                        >
                          <ClipboardCheck className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => setAdjustModal({ isOpen: true, item, type: 'remove' })}
                          className="p-2 hover:bg-red-500/10 hover:text-red-600 dark:text-red-400 rounded-lg transition-colors" 
                          title="Remove Stock"
                          disabled={item.availableQuantity <= 0}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => setAdjustModal({ isOpen: true, item, type: 'add' })}
                          className="p-2 hover:bg-green-500/10 hover:text-green-600 dark:text-green-400 rounded-lg transition-colors" 
                          title="Add Stock"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-6">
          <h3 className="font-medium text-gray-900 dark:text-warm-cream mb-4">Quick Stock Adjustment</h3>
          <p className="text-sm text-gray-600 dark:text-warm-sand mb-4">
            Use the + and - buttons on inventory items to adjust stock.
          </p>
          <div className="flex gap-2 text-sm text-gray-600 dark:text-warm-sand">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-green-500/10 flex items-center justify-center">
                <Plus className="w-3 h-3 text-green-600 dark:text-green-400" />
              </div>
              <span>Add stock</span>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <div className="w-6 h-6 rounded bg-red-500/10 flex items-center justify-center">
                <Minus className="w-3 h-3 text-red-600 dark:text-red-400" />
              </div>
              <span>Remove stock</span>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="font-medium text-gray-900 dark:text-warm-cream mb-4">Recent Transfers</h3>
          <p className="text-gray-600 dark:text-warm-sand text-sm">View transfer history in the Transfers page.</p>
        </div>
      </div>

      {/* Stock Adjustment Modal */}
      <StockAdjustmentModal
        isOpen={adjustModal.isOpen}
        onClose={() => setAdjustModal({ isOpen: false, item: null, type: 'count' })}
        item={adjustModal.item}
        type={adjustModal.type}
      />
    </div>
  );
}
